import { CAMPAIGNS_API_URL, CAMPAIGNS_FILES_FILTER_URL } from 'constants/ApiConstants';
import * as types from 'modules/campaignFiles/actionTypes';
import { filterRequestFormatter } from 'modules/interventions/utils';

/**
 * @param {string} campaignId
 * @param {Partial<import('../types').FetchCampaignFilesQueryParams>} params
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchCampaignFilesAction}
 */
export const fetchCampaignFiles = (campaignId, params, meta = {}) => ({
  type: types.FETCH_CAMPAIGN_FILES,
  payload: {
    request: {
      method: 'GET',
      url: `${CAMPAIGNS_API_URL}/${campaignId}/campaignFiles`,
      params: filterRequestFormatter(params),
    },
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

/**
 * @param {string} campaignId
 * @param {string} campaignFileId
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchCampaignFileAction}
 */
export const fetchCampaignFile = (campaignId, campaignFileId, meta = {}) => ({
  type: types.FETCH_CAMPAIGN_FILE,
  payload: {
    request: {
      method: 'GET',
      url: `${CAMPAIGNS_API_URL}/${campaignId}/campaignFiles/${campaignFileId}`,
      params: {
        projection: 'details',
      },
    },
    campaignId,
    campaignFileId,
  },
  meta: {
    '@app-request': true,
    ...meta,
  },
});

/**
 * @param {string} campaignId
 * @param {string} campaignFileId
 * @param {{ handled: boolean }} params
 * @param {Object} [meta={}]
 * @returns {import("../types").PatchCampaignFileAction}
 */
export const patchCampaignFile = (campaignId, campaignFileId, params, meta = {}) => ({
  type: types.PATCH_CAMPAIGN_FILE,
  payload: {
    campaignFileId,
    request: {
      method: 'PATCH',
      url: `${CAMPAIGNS_API_URL}/${campaignId}/campaignFiles/${campaignFileId}`,
      data: params,
    },
  },
  meta,
});

/**
 * @param {import('../types').CampaignFile} campaignFile
 * @param {Object} [meta={}]
 * @returns {import("../types").PatchCampaignFileSuccessAction}
 */
export const patchCampaignFileSuccess = (campaignFile, meta = {}) => ({
  type: types.PATCH_CAMPAIGN_FILE_SUCCESS,
  payload: campaignFile,
  meta,
});

/**
 * @param {string} campaignFileId
 * @param {string} error
 * @param {Object} [meta={}]
 * @returns {import("../types").PatchCampaignFileFailureAction}
 */
export const patchCampaignFileFailure = (campaignFileId, error, meta = {}) => ({
  type: types.PATCH_CAMPAIGN_FILE_FAILURE,
  payload: { error, campaignFileId },
  error: true,
  meta,
});

/**
 * @param {string} field
 * @param {unknown} value
 * @param {Object} [meta={}]
 * @returns {import("../types").SetCampaignFilesFilterAction}
 */
export const setCampaignFilesFilter = (field, value, meta = {}) => ({
  type: types.SET_CAMPAIGN_FILES_FILTER,
  payload: { field, value },
  meta,
});

/**
 * @param {string} field
 * @param {unknown} value
 * @param {Object} [meta={}]
 * @returns {import("../types").RemoveCampaignFilesFiltersAction}
 */
export const removeCampaignFilesFilter = (field, value, meta = {}) => ({
  type: types.REMOVE_CAMPAIGN_FILES_FILTER,
  payload: { field, value },
  meta,
});

/**
 * @param {unknown} payload
 * @param {Object} [meta={}]
 * @returns {import("../types").ClearCampaignFilesFiltersAction}
 */
export const clearCampaignFilesFilters = (payload, meta = {}) => ({
  type: types.CLEAR_CAMPAIGN_FILES_FILTER,
  payload,
  meta,
});

/**
 * @param {import("copy-webpack-plugin").StringPattern} campaignFileId
 * @param {Object} [meta={}]
 * @returns {import("../types").OpenDetailsDrawerAction}
 */
export const openDetailsDrawer = (campaignFileId, meta = {}) => ({
  type: types.OPEN_DETAILS_DRAWER,
  payload: campaignFileId,
  meta,
});

/**
 * @param {Object} [meta={}]
 * @returns {import("../types").CloseDetailsDrawerAction}
 */
export const closeDetailsDrawer = (meta = {}) => ({
  type: types.CLOSE_DETAILS_DRAWER,
  meta,
});

export const approveManually = (campaignId, campaignFileId, checklistGroupId, data, meta = {}) => {
  const url = checklistGroupId
    ? `${CAMPAIGNS_API_URL}/${campaignId}/campaignFiles/${campaignFileId}/checklistGroups/${checklistGroupId}/accept`
    : `${CAMPAIGNS_API_URL}/${campaignId}/campaignFiles/${campaignFileId}/accept`;

  return ({
    type: types.APPROVE_MANUALLY_REQUEST,
    payload: {
      request: {
        url,
        data,
        method: 'POST',
      },
      campaignFileId,
    },
    meta,
  });
};

/**
 * @param {string} campaignId
 * @param {string} campaignFileId
 * @param {Object} [meta={}]
 * @returns {import("../types").CreateChecklistsAction}
 */
export const createChecklists = (campaignId, campaignFileId, meta = {}) => ({
  type: types.CREATE_CHECKLISTS_REQUEST,
  payload: {
    request: {
      method: 'PUT',
      url: `${CAMPAIGNS_API_URL}/${campaignId}/campaignFiles/${campaignFileId}/checklists`,
    },
    campaignFileId,
  },
  meta,
});

/**
 * @param {Object} params
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchFilteredCampaignFilesAction}
 */
export const fetchFilteredCampaignFiles = ({ mode, ...params }, meta = {}) => ({
  type: types.FETCH_FILTERED_CAMPAIGN_FILES,
  payload: {
    request: {
      method: 'GET',
      url: CAMPAIGNS_FILES_FILTER_URL,
      params: {
        ...params,
        projection: 'page',
        paginationType: 'SLICE',
        sort: 'createdOn,desc',
      },
    },
  },
  meta: {
    ...meta,
    '@app-request': true,
  },
});

/**
 * @param {import("modules/global/generic.types").PageContent<import("../types").CampaignFile>} content
 * @param {Object} [meta={}]
 * @returns {import("../types").FetchFilteredCampaignFilesSuccessAction}
 */
export const fetchFilteredCampaignFilesSuccess = (content, meta = {}) => ({
  type: types.FETCH_FILTERED_CAMPAIGN_FILES_SUCCESS,
  payload: content,
  meta,
});
